import { readConsent } from "@sylviq/ui-react-web/blocks/consent/ConsentBanner";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { getIcon, type IconName } from "@sylviq/ui-react-web/extensions/icons";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { TurnstileChallenge } from "@/features/auth/components/TurnstileChallenge";
import { submitMarketingSignup } from "@/lib/marketing";

// Anchors the logged-out page owns. The header/nav (SiteHeader, NavDrawer)
// and every CTA below must only point at these, at a real route, or at the
// contact address — never at invented pages.
export const MARKETING_SECTION_IDS = [
  "modules",
  "how-it-works",
  "early-access",
] as const;

export const KNOWN_ANCHOR_TARGETS = [
  "#main",
  "#modules",
  "#how-it-works",
  "#early-access",
] as const;

export const KNOWN_ROUTE_TARGETS = ["/", "/login", "/signup", "/products"] as const;

export const KNOWN_EXTERNAL_TARGETS = ["mailto:support@sylviq.io"] as const;

// Every CTA target rendered by the sections below, kept as an explicit list
// so the colocated test can prove no CTA points at an invented route.
export const SECTION_CTA_TARGETS: string[] = [
  "#early-access",
  "/signup",
  "/products",
];

// One tile per real branch of work in the backend today (see
// docs/ROADMAP.md) — this is marketing copy, not the live
// catalog (that's the authenticated launcher, `LauncherPage`, reading
// `GET /crates`). Kept in sync by hand; a stale entry here is a content
// bug, not a functional one.
export const MODULES: Array<{
  /** Product page this card links to (see features/products/catalog.ts). */
  slug: string;
  icon: IconName;
  name: string;
  description: string;
  status?: string;
}> = [
  {
    icon: "receipt",
    slug: "accounting",
    name: "Accounting",
    description:
      "Built in partnership with the accountants at Niche Accounting: double-entry books, invoicing, tax, budgets, and fixed assets — live today.",
    status: "Live today",
  },
  {
    icon: "package",
    slug: "inventory",
    name: "Inventory & operations",
    description: "Stock, receipts, and fulfillment for a physical business.",
  },
  {
    icon: "contact",
    slug: "crm",
    name: "CRM & sales",
    description:
      "Contacts, pipeline, and the customer record everything else hangs off of.",
  },
  {
    icon: "building",
    slug: "workspace",
    name: "Organization & admin",
    description:
      "Legal entities, locations, permissions — one tenancy model.",
  },
];

export const EARLY_ACCESS_POINTS = [
  "One SYLVIQ account and organization for every app — no per-tool accounts to juggle.",
  "One customer record shared across accounting, sales, and operations as they ship.",
  "Install only what you need; the rest joins the same organization.",
];

export const STEPS = [
  {
    title: "Create your account",
    description:
      "One SYLVIQ account creates your organization — no per-app signups.",
  },
  {
    title: "Open Accounting",
    description:
      "Open Accounting, built with the accountants at Niche Accounting — books, invoicing, and tax are live today.",
  },
  {
    title: "Add modules as they ship",
    description:
      "Inventory, CRM, and organization administration share the same records — nothing to re-enter.",
  },
];

// Answers are verbatim sentences from the page body above; the questions only
// ask after them, so no new product claims are introduced here.
export const FAQ_ITEMS = [
  {
    question: "What can I use today?",
    answer:
      "Accounting, built with the accountants at Niche Accounting, is live today — including books, invoicing, and tax.",
  },
  {
    question: "Who built Accounting?",
    answer:
      "SYLVIQ hosts your organization's apps, and Accounting was built in partnership with the accountants at Niche Accounting.",
  },
  {
    question: "Do I need a separate account for every app?",
    answer:
      "One SYLVIQ account creates your organization — no per-app signups.",
  },
  {
    question: "What happens when new modules ship?",
    answer:
      "Inventory, CRM, and organization administration share the same records — nothing to re-enter.",
  },
  {
    question: "Is a credit card required?",
    answer:
      "No. You can create an account and use Accounting without a credit card.",
  },
  {
    question: "What will you do with my email?",
    answer:
      "We only use your email to invite you — no newsletters, no sharing. Joining the list needs analytics or marketing cookies allowed.",
  },
];

export interface ResourceLink {
  label: string;
  description?: string;
  to?: "/login" | "/signup";
  href?: string;
}

// Cards point only at real destinations: the login route, a page anchor, or
// the contact address. Descriptions reuse page-body sentences verbatim.
export const RESOURCE_LINKS: ResourceLink[] = [
  {
    label: "Try accounting today",
    description: "Books, invoicing, and tax are live today and ready to use.",
    to: "/signup",
  },
  {
    label: "Join the waitlist",
    description:
      "Get notified when the next module you need is ready for your organization.",
    href: "#early-access",
  },
  {
    label: "Contact us",
    href: "mailto:support@sylviq.io",
  },
];

export function AccordionItem({
  title,
  defaultOpen = false,
  openOnDesktop = false,
  children,
}: {
  title: ReactNode;
  defaultOpen?: boolean;
  openOnDesktop?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const regionId = useId();
  let contentClassName = "hidden px-5 pb-5";
  if (open) {
    contentClassName = "block px-5 pb-5";
  } else if (openOnDesktop) {
    contentClassName = "hidden px-5 pb-5 md:block";
  }
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--background)]">
      <h3 className="m-0 text-base font-semibold tracking-tight">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={regionId}
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left"
        >
          <span>{title}</span>
          <ChevronDown
            aria-hidden="true"
            className={`size-4 shrink-0 text-[var(--muted-foreground)] transition motion-reduce:transition-none ${open ? "rotate-180" : ""} ${openOnDesktop ? "md:hidden" : ""}`}
          />
        </button>
      </h3>
      <div id={regionId} className={contentClassName}>
        {open || openOnDesktop ? children : null}
      </div>
    </div>
  );
}

export function HeroSection() {
  return (
    <section
      aria-labelledby="hero-title"
      className="mx-auto w-full max-w-2xl px-4 pt-16 pb-12 text-center sm:px-6 sm:pt-24 sm:pb-16"
    >
      <Badge tone="success" className="mb-5">
        Accounting is live today
      </Badge>
      <h1
        id="hero-title"
        className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl"
      >
        One account for your whole back office
      </h1>
      <p className="mx-auto mt-5 max-w-2xl text-base text-pretty text-[var(--muted-foreground)] sm:text-lg">
        SYLVIQ brings accounting, operations, and the rest of your business
        software into one organization — install the apps you need, skip the
        ones you don&apos;t, and stop stitching tools together by hand.
      </p>
      <p className="mx-auto mt-3 max-w-2xl text-sm text-pretty text-[var(--muted-foreground)] sm:text-base">
        SYLVIQ hosts your organization&apos;s apps. Accounting was built in
        partnership with the accountants at Niche Accounting.
      </p>
      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button asChild size="lg" className="w-full rounded-full sm:w-auto">
          <Link to="/signup">Try accounting today</Link>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="w-full rounded-full sm:w-auto"
        >
          <a href="#early-access">Join the waitlist</a>
        </Button>
      </div>
      <p className="mt-4 text-xs text-[var(--muted-foreground)]">
        Accounting is ready now. Inventory, CRM, and more are in development;
        no credit card is required to create an account.
      </p>
      {/* Decorative product preview built from divs — the module names and
          statuses are the same data as the modules section below; hidden
          from assistive tech so it never double-announces. */}
      <div
        aria-hidden="true"
        className="surface mx-auto mt-10 w-full max-w-md rounded-2xl p-4 text-left"
      >
        <div className="flex items-center gap-1.5 px-1 pb-3">
          <span className="size-2.5 rounded-full bg-[var(--muted-strong)]" />
          <span className="size-2.5 rounded-full bg-[var(--muted-strong)]" />
          <span className="size-2.5 rounded-full bg-[var(--muted-strong)]" />
        </div>
        <ul className="grid list-none gap-2 p-0">
          {MODULES.map((module) => {
            const Icon = getIcon(module.icon);
            return (
              <li
                key={module.name}
                className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--background)] px-3 py-2.5"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--action)] text-[var(--action-foreground)]">
                  <Icon className="size-4" />
                </span>
                <span className="text-sm font-medium">{module.name}</span>
                {module.status ? (
                  <Badge tone="success" className="ml-auto">
                    {module.status}
                  </Badge>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function ModulesSection() {
  return (
    <section
      aria-labelledby="modules-title"
      id="modules"
      className="mx-auto w-full max-w-3xl scroll-mt-20 px-4 pb-16 sm:px-6 sm:pb-20"
    >
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
          Modules
        </p>
        <h2
          id="modules-title"
          className="mt-2 text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
        >
          Every branch of the back office, one tenancy model
        </h2>
        <p className="mt-3 text-sm text-[var(--muted-foreground)] sm:text-base">
          Start with what&apos;s live today. The rest joins the same organization
          as it ships — no migrations, no duplicate records.
        </p>
      </div>
      <ul className="mt-8 grid list-none gap-4 p-0 sm:grid-cols-2">
        {MODULES.map((module) => {
          const Icon = getIcon(module.icon);
          return (
            <li
              key={module.name}
              className="surface rounded-2xl p-5 transition motion-reduce:transition-none sm:hover:-translate-y-0.5 sm:motion-reduce:hover:translate-y-0"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--action)] text-[var(--action-foreground)]">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                {module.status ? (
                  <Badge tone="success">{module.status}</Badge>
                ) : null}
              </div>
              <h3 className="mt-4 text-base font-semibold tracking-tight">
                {module.name}
              </h3>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                {module.description}
              </p>
              <Link
                to="/products/$slug"
                params={{ slug: module.slug }}
                className="mt-3 inline-block text-sm font-medium text-[var(--action)] underline-offset-4 hover:underline"
              >
                See what it does
                <span className="sr-only"> — {module.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <div className="mt-8 text-center">
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/products">See all products</Link>
        </Button>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  return (
    <section
      aria-labelledby="how-it-works-title"
      id="how-it-works"
      className="border-y border-[var(--border)] bg-[var(--surface)]"
    >
      <div className="mx-auto w-full max-w-2xl px-4 py-14 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
            How it works
          </p>
          <h2
            id="how-it-works-title"
            className="mt-2 text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
          >
            Up and running in three steps
          </h2>
        </div>
        <div className="mt-8 grid gap-4">
          {STEPS.map((step, index) => (
            <AccordionItem
              key={step.title}
              defaultOpen={index === 0}
              title={
                <span className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--muted)] text-sm font-semibold"
                  >
                    {index + 1}
                  </span>
                  {step.title}
                </span>
              }
            >
              <p className="pl-11 text-sm text-[var(--muted-foreground)]">
                {step.description}
              </p>
            </AccordionItem>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MigrationBand() {
  return (
    <section
      aria-label="No migrations, no duplicate records"
      className="mx-auto w-full max-w-2xl px-4 py-14 text-center sm:px-6"
    >
      <p className="mx-auto max-w-xl text-base text-pretty text-[var(--muted-foreground)] sm:text-lg">
        The rest joins the same organization as it ships — no migrations, no
        duplicate records.
      </p>
      <div className="mt-6 inline-flex flex-col gap-2 rounded-3xl border border-[var(--border)] bg-[var(--surface)] p-1.5 sm:flex-row sm:rounded-full">
        <Button asChild size="sm" className="rounded-full">
          <Link to="/signup">Try accounting today</Link>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <a href="#early-access">Join the waitlist</a>
        </Button>
      </div>
    </section>
  );
}

type SignupStatus = "idle" | "pending" | "prompt-consent" | "success" | "error";

export function EarlyAccessSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<SignupStatus>("idle");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const hintId = useId();
  const statusId = useId();

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const consent = readConsent();
    if (!consent || (!consent.analytics && !consent.marketing)) {
      setStatus("prompt-consent");
      return;
    }

    setStatus("pending");
    setSubmitError(null);
    const result = await submitMarketingSignup(
      email.trim(),
      turnstileToken ?? undefined,
    );
    setStatus(result.ok ? "success" : "error");
    if (!result.ok) {
      setSubmitError(
        result.status === 403
          ? "Verification failed. Complete the challenge and try again."
          : "Something went wrong. Please try again.",
      );
    }
    // A Turnstile token is single-use. Reset the widget after every attempt
    // so a retry cannot submit a token the server has already consumed.
    setTurnstileToken(null);
    setChallengeAttempt((value) => value + 1);
  };

  return (
    <section
      aria-labelledby="early-access-title"
      id="early-access"
      className="mx-auto w-full max-w-xl scroll-mt-20 px-4 py-14 sm:px-6 sm:py-20"
    >
      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex-col items-start gap-1 text-left">
            <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
              Early access
            </p>
            <CardTitle id="early-access-title" className="text-2xl sm:text-3xl">
              Be first in when your module ships
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Tell us where to reach you and we&apos;ll let you know as soon as
              the module you need is ready. Accounting is available now; new
              apps join the same organization as they ship.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid list-none gap-3 p-0">
              {EARLY_ACCESS_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-2 shrink-0 rounded-full bg-[var(--action)]"
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card className="w-full" aria-label="Early access signup">
          <CardHeader className="flex-col items-start gap-1 text-left">
            <CardTitle className="text-base">Get early access</CardTitle>
            <CardDescription>
              Join the early-access list for modules that are still shipping —
              or create an account now and try Accounting today.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-col gap-3"
              onSubmit={submit}
              aria-describedby={hintId}
            >
              <div className="grid gap-2">
                <label className="text-sm font-medium" htmlFor="signup-email">
                  Work email
                </label>
                <Input
                  id="signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  disabled={status === "pending"}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (
                      status === "error" ||
                      status === "prompt-consent" ||
                      status === "success"
                    ) {
                      setStatus("idle");
                    }
                  }}
                  aria-describedby={`${hintId} ${statusId}`}
                />
                <p
                  id={hintId}
                  className="text-xs text-[var(--muted-foreground)]"
                >
                  We only use your email to invite you — no newsletters, no
                  sharing. Joining the list needs analytics or marketing cookies
                  allowed.
                </p>
              </div>
              <TurnstileChallenge
                key={challengeAttempt}
                action="waitlist"
                onToken={setTurnstileToken}
              />
              <Button
                disabled={
                  status === "pending" ||
                  email.trim() === "" ||
                  (Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY) &&
                    !turnstileToken)
                }
                type="submit"
                className="w-full"
              >
                {status === "pending" ? "Signing up…" : "Join the waitlist"}
              </Button>
              <div
                id={statusId}
                aria-live="polite"
                className="min-h-4 empty:hidden"
              >
                {status === "success" ? (
                  <output className="text-xs text-[var(--status-success-foreground)]">
                    You&apos;re on the list — check your inbox soon.
                  </output>
                ) : null}
                {status === "error" ? (
                  <p role="alert" className="text-xs text-[var(--danger)]">
                    {submitError ?? "Something went wrong. Please try again."}
                  </p>
                ) : null}
                {status === "prompt-consent" ? (
                  <output className="text-xs text-[var(--muted-foreground)]">
                    Please allow analytics or marketing cookies to sign up — use
                    the cookie banner to choose, then try again.
                  </output>
                ) : null}
              </div>
              <Button asChild size="sm" variant="ghost" className="self-center">
                <Link to="/signup">Ready to start? Create an account</Link>
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section
      aria-labelledby="faq-title"
      className="mx-auto w-full max-w-2xl px-4 pb-14 sm:px-6 sm:pb-20"
    >
      <div className="mx-auto max-w-2xl text-center">
        <h2
          id="faq-title"
          className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
        >
          Frequently asked questions
        </h2>
      </div>
      <div className="mt-8 grid gap-4">
        {FAQ_ITEMS.map((item, index) => (
          <AccordionItem
            key={item.question}
            defaultOpen={index === 0}
            title={item.question}
          >
            <p className="text-sm text-[var(--muted-foreground)]">
              {item.answer}
            </p>
          </AccordionItem>
        ))}
      </div>
    </section>
  );
}

export function ResourcesSection() {
  return (
    <section
      aria-labelledby="resources-title"
      className="mx-auto w-full max-w-2xl px-4 pb-16 sm:px-6 sm:pb-20"
    >
      <div className="mx-auto max-w-2xl text-center">
        <h2
          id="resources-title"
          className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
        >
          Get started
        </h2>
      </div>
      <ul className="mt-8 grid list-none gap-4 p-0">
        {RESOURCE_LINKS.map((resource) => {
          const body = (
            <>
              <span className="flex items-center justify-between gap-3">
                <span className="text-base font-semibold tracking-tight">
                  {resource.label}
                </span>
                <ArrowUpRight
                  aria-hidden="true"
                  className="size-4 shrink-0 text-[var(--muted-foreground)]"
                />
              </span>
              {resource.description ? (
                <span className="mt-1 block text-sm text-[var(--muted-foreground)]">
                  {resource.description}
                </span>
              ) : null}
            </>
          );
          return (
            <li key={resource.label}>
              {resource.to ? (
                <Link
                  to={resource.to}
                  className="surface block rounded-2xl p-5 transition motion-reduce:transition-none sm:hover:-translate-y-0.5 sm:motion-reduce:hover:translate-y-0"
                >
                  {body}
                </Link>
              ) : (
                <a
                  href={resource.href}
                  className="surface block rounded-2xl p-5 transition motion-reduce:transition-none sm:hover:-translate-y-0.5 sm:motion-reduce:hover:translate-y-0"
                >
                  {body}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function MarketingSections() {
  return (
    <>
      <HeroSection />
      <ModulesSection />
      <HowItWorksSection />
      <MigrationBand />
      <EarlyAccessSection />
      <FaqSection />
      <ResourcesSection />
    </>
  );
}
